import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAppState } from "contexts/AppContext";
import { loadPlatformDomainTokens } from "hooks/usePlatformDomainTokens";
import { useSwitchPlatform } from "hooks/useSwitchPlatform";

import { PushNotificationActionPerformed } from "../IPushNotificationAdapter";
import { extractNotificationUrl } from "../utils/extractNotificationUrl";

export const useDeepLinkNavigation = () => {
  const navigate = useNavigate();
  const { platformId } = useAppState();
  const { switchToPlatform } = useSwitchPlatform();
  // The push context re-registers its native listeners whenever this handler changes identity, so
  // the one value that changes on a practice switch is read through a ref instead of a dependency.
  const currentPlatformId = useRef(platformId);
  currentPlatformId.current = platformId;

  const handleNotificationAction = useCallback(
    async (actionPerformed: PushNotificationActionPerformed<unknown>) => {
      const { notification } = actionPerformed;

      console.log("[RMD]: handleNotificationAction", actionPerformed);

      const url = extractNotificationUrl(notification);

      if (!url) {
        return;
      }

      if (!isInternalAppRoute(url)) {
        console.log("[RMD]: Ignoring external or API URL from notification:", url);
        return;
      }

      // A notification can target a practice the patient is not currently acting as — an arrival
      // nudge fires for whichever practice they walked into. Switch first so the screen the
      // notification opens loads against that practice's API host and branding.
      const targetPlatformId = extractPlatformId(url);

      if (targetPlatformId && targetPlatformId !== currentPlatformId.current) {
        const platforms = await loadPlatformDomainTokens();
        const targetPlatform = platforms.find(
          (platform) => platform.platformId === targetPlatformId,
        );

        if (targetPlatform) {
          await switchToPlatform(targetPlatform, platforms);
        } else {
          // The patient has no token for that practice, so we cannot act as them there. Opening
          // the route on the current practice is still better than dropping the notification.
          console.log("[RMD]: No platform token for notification target:", targetPlatformId);
        }
      }

      navigate(url);
    },
    [navigate, switchToPlatform],
  );

  return { handleNotificationAction };
};

function extractPlatformId(url: string): string | null {
  const queryStart = url.indexOf("?");

  if (queryStart === -1) {
    return null;
  }

  return new URLSearchParams(url.slice(queryStart)).get("platformId");
}

function isInternalAppRoute(url: string): boolean {
  if (url.includes("://")) return false;

  if (url.startsWith("/api/")) return false;

  return true;
}
